import React from 'react'
import { track } from '@SonarCX/lib/mixpanel'
import { useAppDispatch } from '@SonarCX/hooks/redux'
import { updateFavoritesOrder } from '@SonarCX/slices/favorites'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'

import FavoriteCard from '@SonarCX/components/molecules/FavoriteCard'
import FavoritesFilter from '../FavoritesFilter'
import useSavedValue from '@SonarCX/hooks/useSavedValue'
import useFavorites from '@SonarCX/hooks/useFavorites'

import useStyles from '../../styles'

const FavoritesColumn = () => {
  const dispatch = useAppDispatch()
  const { user } = useEnvironment()

  const [searchValue, setSearchValue] = React.useState('')
  const [favoriteType, setFavoriteType] = useSavedValue<string>({
    key: 'favoriteType',
    defaultValue: '"All"'
  })
  const { allFavorites, loaded } = useFavorites()

  const filteredFavorites = React.useMemo(() => {
    if (favoriteType === 'All') return allFavorites

    switch (favoriteType) {
      case 'Conversations':
        return allFavorites.filter(favorite => favorite.type === 'session')
      case 'Team chats':
        return allFavorites.filter(favorite => favorite.type === 'peer_chat')
      case 'Tasks':
        return allFavorites.filter(favorite => favorite.type === 'task')
      case 'Contacts':
      case 'Team':
        return allFavorites.filter(favorite => favorite.type === 'user')
      case 'Articles':
        return allFavorites.filter(favorite => favorite.type === 'article')
      default:
        return allFavorites
    }
  }, [allFavorites, favoriteType])
  const classes = useStyles()

  const handleFavoriteTypeChange = (type: string) => {
    setFavoriteType(type)
  }

  React.useEffect(() => {
    track('FAVORITES_TAB_VIEWED')
  }, [])

  const isDraggable = favoriteType === 'All' && searchValue.length === 0

  const handleMoveFavorite = React.useCallback(
    (start, end) => {
      if (start !== end && user.id) {
        // swap favorites by index
        const startIndex = allFavorites.findIndex(
          favorite => favorite.item_id === start
        )
        const endIndex = allFavorites.findIndex(
          favorite => favorite.item_id === end
        )

        const favCopy = [...allFavorites]
        const [removed] = favCopy.splice(startIndex, 1)
        favCopy.splice(endIndex, 0, removed)
        dispatch(updateFavoritesOrder(user.id, favCopy))
        track('FAVORITES_REORDERED')
      }
    },
    [allFavorites, dispatch, user]
  )

  if (!loaded) return null

  if (allFavorites.length === 0)
    return <div className={classes.subHeader}>No Favorites, yet!</div>

  return (
    <>
      <h4 className={classes.subHeader}>My favorites</h4>

      <div>
        <FavoritesFilter
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          favoriteType={favoriteType}
          handleFavoriteTypeChange={handleFavoriteTypeChange}
        />
        {filteredFavorites.map(favorite => (
          <FavoriteCard
            draggable={isDraggable}
            handleMoveFavorite={handleMoveFavorite}
            key={favorite.item_id}
            favorite={favorite}
            filter={favoriteType}
            searchValue={searchValue}
          />
        ))}
      </div>
    </>
  )
}
export default FavoritesColumn
